import { RadioGroup } from '@mui/material';
import type { ReactNode } from 'react';

import FormGroup from '../../../generic/Forms/FormGroup';
import LabeledRadio from '../../../generic/Forms/LabeledRadio';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import Text from '../../../generic/Text';
import Header from '../../../shared/settings/SettingsPageHeader';
import type { RolePermissions } from '../../../../../shared/model-typings';
import type { RoleScope } from '../../../../../typings/enums';

import AllPermissionsTable from './AllPermissionsTable';
import type { AllPermissionsItem } from './hooks/useAllPermissions';
import type { EditingRole } from './types';

type AddEditRoleSectionProps = {
  heading: string;
  subheading?: string;
  children: ReactNode;
};

/**
 * AddEditRoleSection represents one of the three "sections" of the page.
 *
 * @param {AddEditRoleSectionProps} props - The props for the AddEditRoleSection.
 * @param {string} props.heading - H2 heading for the section.
 * @param {string} props.subheading - Optional paragraph text for under the heading.
 * @param {ReactNode} props.children - Section contents.
 */
const AddEditRoleSection = ({ heading, subheading, children }: AddEditRoleSectionProps) => (
  <section className="roles__add-edit-role__section">
    <Text.H2>{heading}</Text.H2>
    {subheading && <Text.Paragraph>{subheading}</Text.Paragraph>}
    {children}
  </section>
);

type AddEditRoleProps = {
  name: string;
  description: string;
  scope: RoleScope;
  permissions: RolePermissions;
  isAddMode: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  updateEditingRole: (updatedEditingRole: Partial<EditingRole>) => void;
  onCancelClick: () => void;
  onSaveClick: () => void;
};

const AddEditRole = ({
  name,
  description,
  scope,
  permissions,
  isAddMode,
  isReadOnly,
  isSaving,
  updateEditingRole,
  onCancelClick,
  onSaveClick,
}: AddEditRoleProps) => (
  <form className="roles__add-edit-role">
    <Header heading={isAddMode ? 'Add Role' : `${isReadOnly ? 'Viewing' : 'Editing'} "${name}" Role`}>
      <Header.Button
        type="secondary"
        size="medium"
        onClick={onCancelClick}
        isDisabled={isSaving}
      >
        Cancel
      </Header.Button>
      {!isReadOnly && (
        <Header.Button
          type="primary"
          size="medium"
          onClick={onSaveClick}
          isDisabled={isSaving || !(name && description)}
        >
          {isSaving ? 'Saving' : 'Save'}
        </Header.Button>
      )}
    </Header>
    <AddEditRoleSection heading="Details">
      <FormGroup spacing="lg">
        <LabeledTextInput
          labelVariant="legacy"
          label="Role name"
          value={name}
          placeholder="Role name"
          onChange={(updatedName: string) => !isReadOnly && updateEditingRole({ name: updatedName })}
          isDisabled={isReadOnly}
          isRequired
        />
        <LabeledTextInput
          labelVariant="legacy"
          label="Role description"
          value={description}
          placeholder="Role description"
          onChange={(updatedDescription: string) =>
            !isReadOnly && updateEditingRole({ description: updatedDescription })}
          isDisabled={isReadOnly}
          isRequired
        />
      </FormGroup>
    </AddEditRoleSection>
    <hr className="roles__add-edit-role__divider" />
    <AddEditRoleSection heading="Type" subheading="Role will be for:">
      <RadioGroup
        value={scope}
        onChange={(_event, updatedScope) =>
          //  Check for add mode, because you cannot change the scope of a role once it's been created.
          isAddMode && !isReadOnly && updateEditingRole({ scope: updatedScope as RoleScope })}
      >
        <FormGroup spacing="xs">
          <LabeledRadio
            label="Organization"
            value="organization"
            isDisabled={!isAddMode || isReadOnly}
            isRippleDisabled
          />
          <LabeledRadio
            label="Team"
            value="team"
            isDisabled={!isAddMode || isReadOnly}
            isRippleDisabled
          />
        </FormGroup>
      </RadioGroup>
    </AddEditRoleSection>
    <hr className="roles__add-edit-role__divider" />
    <AddEditRoleSection heading="Permissions" subheading="Inside the organization or team">
      <AllPermissionsTable
        isReadOnly={isReadOnly}
        scope={scope}
        rolePermissions={permissions}
        onPermissionsChange={(updatedPermissions: AllPermissionsItem[]) =>
          updateEditingRole({ permissions: updatedPermissions })}
      />
    </AddEditRoleSection>
  </form>
);

export default AddEditRole;
